import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft } from 'lucide-react';
import lanerunnerLogoWhite from '@/assets/lanerunner-logo-white.png';
import lanerunnerLogoBlack from '@/assets/lanerunner-logo-black.png';
import { useTheme } from 'next-themes';

export default function ForgotPassword() {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Check rate limit
      const { data: rateLimitData, error: rateLimitError } = await supabase
        .rpc('check_rate_limit', {
          p_identifier: email,
          p_action_type: 'password_reset',
          p_max_attempts: 3,
          p_window_minutes: 60
        });

      if (rateLimitError) {
        console.error('Rate limit check error:', rateLimitError);
      }

      if (rateLimitData && typeof rateLimitData === 'object' && 'allowed' in rateLimitData && !rateLimitData.allowed) {
        const blockedUntil = new Date((rateLimitData as any).blocked_until);
        const minutesRemaining = Math.ceil((blockedUntil.getTime() - Date.now()) / 60000);
        
        toast({
          title: "Too many attempts",
          description: `Please try again in ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}.`,
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }

      // Call our custom edge function to send password reset email via Resend
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: { email: email.toLowerCase() }
      });

      // Detect server-side rate limit (status 429 or { error: 'rate_limited' })
      const rateLimited =
        (data && typeof data === 'object' && (data as any).error === 'rate_limited') ||
        (error && (error as any).context?.status === 429);

      if (rateLimited) {
        toast({
          title: "Too many attempts",
          description: "Too many attempts. Please try again later.",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }

      if (error) {
        console.error('Password reset error:', error);
        // Fail neutral — never leak internal details
      }

      setEmailSent(true);
      toast({
        title: "Reset email sent",
        description: "If that email exists in our system, we've sent reset instructions."
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-lr-dark via-lr-dark-secondary to-lr-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="mb-8 text-center">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={theme === 'dark' ? lanerunnerLogoWhite : lanerunnerLogoBlack} 
                alt="LaneRunner" 
                className="h-10 mx-auto"
              />
            </Link>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Reset Your Password
            </h1>
            <p className="text-muted-foreground">
              {emailSent 
                ? "Check your email for reset instructions" 
                : "Enter your email address and we'll send you a reset link"
              }
            </p>
          </div>

          {!emailSent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12"
                  required
                  autoFocus
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12" 
                disabled={isLoading}
                variant="lr-cyan"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <div className="text-center">
                <Link 
                  to="/auth" 
                  className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Link>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="bg-muted/50 border border-border rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  We've sent password reset instructions to <strong className="text-foreground">{email}</strong>
                </p>
              </div>

              <div className="space-y-3">
                <Link to="/auth" className="block">
                  <Button variant="ghost" className="w-full">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          LaneRunner © 2024. All Rights Reserved
        </p>
      </div>
    </div>
  );
}
